/**
 * Shared color constants for analytics UIs (signals, playbooks performance,
 * teammate performance, etc).
 *
 * Keep these centralized so charts across features stay visually consistent.
 */

/**
 * A three-tone color scheme for chart segments:
 * - `fill`   — the pastel body color (soft, low-saturation)
 * - `stroke` — a slightly darker outline so segments remain crisp against
 *              light backgrounds
 * - `dot`    — the bold marker color used for legends / single-point callouts
 *
 * This combination produces the "soft fill + outline" look shared by the
 * playbooks analytics bar charts.
 */
export interface ChartColorScheme {
  fill: string;
  stroke: string;
  dot: string;
}

/**
 * Categorical palette for charts whose series are unordered categories: the
 * performance-report discrete bar charts and the HQ AI cost / AI credit usage
 * stacks.
 */
export const SERIES_COLORS: readonly string[] = [
  "var(--color-emerald-500)",
  "var(--color-sky-500)",
  "var(--color-amber-500)",
  "var(--color-violet-500)",
  "var(--color-rose-500)",
  "var(--color-teal-500)",
  "var(--color-orange-500)",
  "var(--color-indigo-500)",
];

/**
 * Outcome category → solid color for charts that need a single bold color
 * per outcome (e.g. the multi-line trend chart). Matches the
 * `green/red/yellow/gray` result category system in
 * `@repo/app-types/entities/outcome`.
 */
export const outcomeColorMap: Record<string, string> = {
  green: "var(--color-green-500)",
  red: "var(--color-red-500)",
  yellow: "var(--color-yellow-500)",
  gray: "var(--color-gray-500)",
};

/**
 * Outcome category → three-tone color scheme for stacked/distribution bar
 * charts (e.g. signal outcome impact, outcome-by-criteria-score).
 */
export const outcomeColorSchemeMap: Record<string, ChartColorScheme> = {
  green: {
    fill: "var(--color-green-300)",
    stroke: "var(--color-green-400)",
    dot: "var(--color-green-500)",
  },
  red: {
    fill: "var(--color-red-300)",
    stroke: "var(--color-red-400)",
    dot: "var(--color-red-500)",
  },
  yellow: {
    fill: "var(--color-yellow-300)",
    stroke: "var(--color-yellow-400)",
    dot: "var(--color-yellow-500)",
  },
  gray: {
    fill: "var(--color-gray-300)",
    stroke: "var(--color-gray-400)",
    dot: "var(--color-gray-500)",
  },
};
